import { Box, COLOR_PALETTE, Text } from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { USERS_URL } from '../../../../constants/urls';
import useCheckMyProfile from '../../../../hooks/useCheckMyProfile';
import { useCurrentUrl } from '../../../../hooks/useCurrentUrl';
import useInstructorProfileQuery from '../../../../hooks/useInstructorProfileQuery';
import useQueryParams from '../../../../hooks/useQueryParams';
import useUserProfileQuery from '../../../../hooks/useUserProfileQuery';

export function UserTabs() {
  const { id } = useQueryParams();
  const { data: profile } = useUserProfileQuery({ id: Number(id) });

  if (!profile) {
    return null;
  }

  const isInstructor = profile.isInstructor;

  return isInstructor ? <InstructorTabs /> : <StudentTabs />;
}

function StudentTabs() {
  const { t } = useTranslation();
  const { isKo } = useLang();
  const { currentUrlWithoutQueryString } = useCurrentUrl();

  const { id } = useQueryParams();
  const { isMe } = useCheckMyProfile();

  const { data: profile } = useUserProfileQuery({ id: Number(id) });
  const hasRoadmap = profile?.hasRoadmaps;
  const hasClip = profile?.hasClips;
  const hasBlog = profile?.hasBlogs;
  const hasPost = profile?.hasPosts;
  const userSlug = profile?.slug;

  const NAVIGATION_LINKS = [
    {
      label: t('홈'),
      path: '',
      getHref: (userId: string, slug?: string) => USERS_URL.HOME({ userId, slug }),
      enabled: true,
    },
    {
      label: '클립',
      path: 'clips',
      getHref: (userId: string) => USERS_URL.CLIPS(userId),
      enabled: (isMe || hasClip) && isKo,
    },
    {
      label: t('로드맵'),
      path: 'roadmaps',
      getHref: (userId: string) => USERS_URL.ROADMAPS(userId),
      enabled: (isMe || hasRoadmap) && isKo,
    },
    {
      label: t('게시글'),
      path: 'posts',
      getHref: (userId: string) => USERS_URL.POSTS(userId),
      enabled: isMe || hasPost,
    },
    {
      label: t('블로그'),
      path: 'blogs',
      getHref: (userId: string) => USERS_URL.BLOGS(userId),
      enabled: (isMe || hasBlog) && isKo,
    },
  ];

  return (
    <nav>
      <TabList>
        {NAVIGATION_LINKS.filter(({ enabled }) => enabled).map(({ label, getHref }) => {
          const href = getHref(String(id), userSlug);
          const isActive = currentUrlWithoutQueryString === href;

          return <TabItem key={label} isActive={isActive} href={href} label={label} />;
        })}
      </TabList>
    </nav>
  );
}

function InstructorTabs() {
  const { t } = useTranslation();
  const { isKo } = useLang();
  const { currentUrlWithoutQueryString } = useCurrentUrl();

  const { id } = useQueryParams();
  const { isMe } = useCheckMyProfile();

  const { data: profile } = useUserProfileQuery({ id: Number(id) });
  const { data: instructorProfile } = useInstructorProfileQuery({
    userId: String(id),
    isInstructor: true,
  });
  const hasRoadmap = instructorProfile?.hasRoadmap;
  const hasCourse = !!instructorProfile?.courseCount;
  const hasReview = !!instructorProfile?.reviewCount;
  const hasClip = profile?.hasClips;
  const hasBlog = profile?.hasBlogs;
  const hasPost = profile?.hasPosts;
  const userSlug = profile?.slug;

  const NAVIGATION_LINKS = [
    {
      label: t('홈'),
      path: '',
      getHref: (userId: string, slug?: string) => USERS_URL.HOME({ userId, slug }),
      enabled: true,
    },
    {
      label: t('강의'),
      path: 'courses',
      getHref: (userId: string) => USERS_URL.COURSES(userId),
      enabled: hasCourse,
    },
    {
      label: '클립',
      path: 'clips',
      getHref: (userId: string) => USERS_URL.CLIPS(userId),
      enabled: (isMe || hasClip) && isKo,
    },
    {
      label: t('로드맵'),
      path: 'roadmaps',
      getHref: (userId: string) => USERS_URL.ROADMAPS(userId),
      enabled: (isMe || hasRoadmap) && isKo,
    },
    {
      label: t('수강평'),
      path: 'reviews',
      getHref: (userId: string) => USERS_URL.COURSE_REVIEWS(userId),
      enabled: hasReview,
    },
    {
      label: t('게시글'),
      path: 'posts',
      getHref: (userId: string) => USERS_URL.POSTS(userId),
      enabled: isMe || hasPost,
    },
    {
      label: t('블로그'),
      path: 'blogs',
      getHref: (userId: string) => USERS_URL.BLOGS(userId),
      enabled: (isMe || hasBlog) && isKo,
    },
  ];

  return (
    <nav>
      <TabList>
        {NAVIGATION_LINKS.filter(({ enabled }) => enabled).map(({ label, getHref }) => {
          const href = getHref(String(id), userSlug);
          const isActive = currentUrlWithoutQueryString === href;

          return <TabItem key={label} isActive={isActive} href={href} label={label} />;
        })}
      </TabList>
    </nav>
  );
}

function TabList({ children }: { children: ReactNode }) {
  return (
    <Box
      component="ul"
      py={16}
      css={{
        padding: 0,
        display: 'flex',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },

        li: {
          padding: '14px 16px',
          whiteSpace: 'nowrap',
        },
      }}
    >
      {children}
    </Box>
  );
}

function TabItem({
  href,
  isActive,
  label,
}: {
  href: string;
  isActive: boolean;
  label: string;
}) {
  return (
    <Box
      component="li"
      py={8}
      px={12}
      css={{
        borderBottom: `2px solid ${isActive ? COLOR_PALETTE.infgreen[7] : 'transparent'}`,
      }}
    >
      <Link
        href={href}
        style={{ display: 'block' }}
        aria-label={isActive ? `현재 ${label}페이지` : `${label}페이지로 이동`}
        aria-current={isActive ? 'page' : undefined}
        onClick={(e) => {
          if (isActive) {
            e.preventDefault();
          }
        }}
      >
        <Text component="span" weight={isActive ? 700 : 500} color={isActive ? 'gray.9' : 'gray.5'}>
          {label}
        </Text>
      </Link>
    </Box>
  );
}
